import GlobalError from "@/components/pages/global-error";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import {
	createWSClient,
	httpLink,
	loggerLink,
	splitLink,
	wsLink,
} from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { Loader2 } from "lucide-react";
import SuperJson from "superjson";
import type { AppRouter } from "trpc-yehey_live/src/routes.ts";

import { createTRPCClient } from "@trpc/react-query";

import type { RouteContext as TanStackRouteContext } from "@tanstack/react-router";

// Import the generated route tree
import { routeTree } from "./routeTree.gen.ts";
import { auth } from "@/lib/better-auth.ts";
import { queryClient } from "@/lib/query-client.ts";

const storage = {
	getItem: (name: string) => {
		const str = localStorage.getItem(name);
		if (!str) return null;
		return SuperJson.parse(str);
	},
	setItem: (name: string, value: any) => {
		localStorage.setItem(name, SuperJson.stringify(value));
	},
	removeItem: (name: string) => localStorage.removeItem(name),
};

const localStoragePersister = createSyncStoragePersister({
	storage: storage as any,
});

persistQueryClient({
	queryClient,
	persister: localStoragePersister,
});

export const wsTrpcClient = createWSClient({
	url: `${import.meta.env.VITE_TRPC_WS_SERVER}`,
	connectionParams: async () => {
		const token = sessionStorage.getItem("TOKEN") || ""

		return {
			"Yehey-Live-Cookie": `${token}`,
			Authorization: `${token}`,
		};
	},
	retryDelayMs: (a) => 1000 * a,
});

export const trpcClient = createTRPCClient<AppRouter>({
	links: [
		loggerLink({
			enabled: () => import.meta.env.DEV,
		}),
		splitLink({
			condition: (op) => {
				return op.type === "subscription";
			},
			true: wsLink({
				client: wsTrpcClient,
				transformer: SuperJson,
			}),
			false: httpLink({
				url: import.meta.env.VITE_TRPC_SERVER!,
				headers: () => {
					const token = sessionStorage.getItem("TOKEN") || ""

					return {
						"Yehey-Live-Cookie": `${token}`,
						Authorization: `${token}`,
						"Content-Type": "application/json",
						// Add CORS headers
						"Access-Control-Allow-Origin": "*",
						"Access-Control-Allow-Methods": "GET, POST, PUT, DELETE, OPTIONS",
						"Access-Control-Allow-Headers": "Content-Type, Authorization",
					};
				},
				transformer: SuperJson,
			}),
		}),
	],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
	client: trpcClient,
	queryClient,
});


export function createRouter() {
	const router = createTanStackRouter({
		routeTree,
		scrollRestoration: true,
		defaultPreload: "intent",
		defaultStructuralSharing: true,
		defaultPreloadStaleTime: 0,
		defaultErrorComponent: GlobalError,
		context: {
			trpc,
			queryClient,
			auth: {
				data: null,
				isPending: true,
			} as {
				data: ReturnType<typeof auth.useSession>["data"]
				isPending: boolean;
			},
		},
		defaultPendingComponent: () => (
			<div className={"p-2 text-2xl"}>
				<Loader2 className="w-4 h-4 animate-spin" />
			</div>
		),
		Wrap: function WrapComponent({ children }) {
			return (
				<QueryClientProvider client={queryClient}>
					{children}
				</QueryClientProvider>
			);
		},
	});

	return router;
}

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof createRouter>;
	}
}


export type ContextRouter = TanStackRouteContext & {
	trpc: ReturnType<typeof createTRPCOptionsProxy<AppRouter>>;
	queryClient: QueryClient;
	auth: {
		data: ReturnType<typeof auth.useSession>["data"];
		isPending: boolean;
	};
};
